import "dayjs/locale/ja";
import { useSelector } from "react-redux";
import { RootState } from "../../store/types";
import { Button, Col, Form, List, Row, Table, Tabs, Tag } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { CloseOutlined, EditOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import HrnModal from "../../components/HrnModal";
import validateMessages from "../../utils/validate";
import { useEffect, useRef, useState } from "react";
import SerialForm from "../Customer/SerialForm";
import { useBookServiceMutation, useLazyGetMyBookedServicesQuery, useLazyGetServicesQuery, useUpdateServiceMutation } from "../../services/service";
import tableUtils from "../../utils/tableUtils";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import { SerialStatus, SerialStatusTagColor } from "../../shared/constants";
import useScreenSize from "../../hooks/useScreenSize";
import HrnAvatar from "../../components/HrnAvatar";
import dateUtils from "../../utils/dateUtils";

const SerialBooking = () => {
    const location = useLocation();
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const { isMobile } = useScreenSize();
    const navigate = useNavigate();
    const [serialForm] = Form.useForm();
    const [cancelForm] = Form.useForm();
    const [selectedService, setSelectedService] = useState<any | null>(null);
    const [open, setOpen] = useState(false);
    const [openCancelForm, setOpenCancelForm] = useState(false);
    const [myServices, setMyServices] = useState<any[]>([]);
    const intervalRef = useRef<any>(null);
    const [getBookedServices, getBookedServicesResult] = useLazyGetMyBookedServicesQuery();
    const [getServiceCenterServices, getServiceCenterServicesResult] = useLazyGetServicesQuery();
    const [bookForSerial, bookResult] = useBookServiceMutation();
    const [updateSerial, updateResult] = useUpdateServiceMutation();

    const startServicesFetching = async () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        await getBookedServices({});

        intervalRef.current = setInterval(async () => {
            await getBookedServices({})
        }, 10000);
    };

    useEffect(() => {
        startServicesFetching();
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [location.pathname]);

    useEffect(() => {
        if (getBookedServicesResult.isSuccess) {
            setMyServices(getBookedServicesResult.data || []);
        }
    }, [getBookedServicesResult.isFetching]);

    const openSerialDialog = () => {
        setOpen(true);
    };

    const closeSerialDialog = () => {
        setOpen(false);
        serialForm.resetFields();
        setSelectedService(null);
    };
    const openSerialEditDialog = (service: any) => {
        setSelectedService(service);
        serialForm.setFieldsValue({
            mode: "Edit",
            ...service,
            serviceTaker: service.forSelf ? "Self" : "Other"
        });
        setOpen(true);
    };

    const createSerial = async () => {
        try {
            await serialForm.validateFields();
            const formFields = serialForm.getFieldsValue();
            if (selectedService == null) {
                await bookForSerial({
                    serviceCenterId: formFields.serviceCenterId,
                    body: {
                        ...formFields,
                        forSelf: formFields.serviceTaker === "Self",
                        serviceCenter: undefined
                    }
                }).unwrap();
            } else {
                await updateSerial({
                    serviceCenterId: selectedService?.serviceCenterId,
                    id: selectedService?.id,
                    body: {
                        serviceTypeId: formFields.serviceTypeId,
                        forSelf: formFields.serviceTaker === "Self",
                        name: formFields.name,
                        contactNo: formFields.contactNo
                    }
                }).unwrap();
            }
            setOpen(false);
            serialForm.resetFields();
            setSelectedService(null);
        } catch (errorInfo) {
            return;
        }
    };

    const openCancelDialog = (service: any) => {
        setOpenCancelForm(true);
        setSelectedService(service);
    };

    const closeCancelDialog = () => {
        setOpenCancelForm(false);
        setSelectedService(null);
        cancelForm.resetFields();
    };

    const cancelSerial = async () => {
        try {
            await cancelForm.validateFields();
            const formFields = cancelForm.getFieldsValue();
            await updateSerial({
                serviceCenterId: selectedService?.serviceCenterId,
                id: selectedService?.id,
                body: {
                    ...formFields,
                    serviceTypeId: selectedService?.serviceTypeId,
                    status: "Cancelled"
                }
            }).unwrap();
            setOpenCancelForm(false);
            cancelForm.resetFields();
        } catch (errorInfo) {
            return;
        }
    };
    const approxPresentTime = (date: any, approxTime: any) => {
        const time = dayjs(approxTime);
        const result = dayjs(date).hour(time.hour()).minute(time.minute()).second(time.second());
        return result.add(dayjs().utcOffset(), "minute");
    };
    const [serviceCenter4Detail, setServiceCenter4Detail] = useState<any>(null);
    const [queueServices, setQueueServices] = useState<any[]>([]);
    const [servedServices, setServedServices] = useState<any[]>([]);
    const openServiceCenterDialog = async (service: any) => {
        setServiceCenter4Detail(service);
        await getServiceCenterServices({
            serviceCenterId: service.serviceCenterId,
            serviceDate: dayjs(service.date)?.format("YYYY-MM-DD")
        }).unwrap();
    };

    const closeServiceCenterDialog = async () => {
        setServiceCenter4Detail(null);
    };
    useEffect(() => {
        if (getServiceCenterServicesResult.isSuccess) {
            const services = getServiceCenterServicesResult.data || [];
            const queue = services.filter((x: any) => x.status !== SerialStatus.SERVED);
            const served = services.filter((x: any) => x.status === SerialStatus.SERVED).sort((x: any, y: any) => new Date(y.statusTime).getTime() - new Date(x.statusTime).getTime());
            setQueueServices(queue);
            setServedServices(served);
        }
    }, [getServiceCenterServicesResult.isFetching]);

    const renderSerialTemplate = (record: any, index: number) => (
        <List.Item key={record.id} className="!p-0">
            <Row className="w-full" >
                <Col className="w-[30px]">
                    <h2 className="mt-1 text-primary">{record.serialNo}.</h2>
                </Col>
                <Col style={{ width: "calc(100% - 90px)" }}  >
                    <List.Item.Meta
                        avatar={<HrnAvatar user={record.user} size={"large"} />}
                        title={<>
                            {record.forSelf && <span className="text-primary" >{record.user?.name}</span>}
                            {!record.forSelf && <span className="text-primary" >{record.name}</span>}
                        </>
                        }
                        description={<div className="mt-[-10px]">
                            <small>{dayjs(record.createdTime + "Z").format("ddd hh:mm:ss A")}</small>
                            {/* {!record.forSelf && <small className="text-[#ccc] whitespace-nowrap ml-[3px] max-w-[100px] inline-block text-ellipsis overflow-hidden">(By {record.isAdmin ? "Admin" : record.user.name})</small>} */}
                            <div>{record.serviceType.name}</div>
                        </div>}
                    />
                </Col>
                <Col className="w-[60px]">
                    <Button disabled type="text" className="m-0 p-0" ><Tag className="m-0" color={SerialStatusTagColor[record.status as SerialStatus]}>{record.status}</Tag></Button>
                    <div style={{ fontSize: "0.5rem", whiteSpace: "nowrap" }}>
                        {dayjs(record.statusTime + "Z").format("ddd hh:mm:ss A")}
                    </div>
                    <div className="text-right" style={{ fontSize: "0.7rem" }}>{record.charge && `${record.charge} BDT`}</div>
                </Col>
            </Row>
        </List.Item>
    );
    const servicesColumn: any[] = [
        {
            key: "name",
            header: "Name",
            className: " font-medium",
            fn: (value: any, record: any) => (<>
                {record.company?.businessTypeId === 1 && <div>{record.company.name}</div>}
                <div className="text-primary"><Button type="link" onClick={() => openServiceCenterDialog(record)} className="text-primary p-0 m-0 pl-[2px] pr-[5px]">{record.serviceCenter.name}</Button></div>
                {!record.forSelf && <div>For {record.name}</div>}
                <div className="w-full">
                    {record.serviceType.name} ({record.serialNo})
                </div>
                <div className="w-full">
                    {approxPresentTime(record.date, record.approxServeTime) < dayjs() && <span className="mr-1" title={dayjs(record.approxServeTime).add(dayjs().utcOffset(), "minute").format("DD MMM YYYY h:mm A")}>Approx. Time: {dateUtils.formatDateTime(dayjs(record.approxServeTime).add(dayjs().utcOffset(), "minute"))}</span>}
                </div>
            </>)
        },
        {
            key: "id",
            header: "Actions",
            className: "w-[120px]",
            fn: (value: any, record: any) => (<div className="text-right">
                <Tag className="m-0" color={SerialStatusTagColor[record.status as SerialStatus]}>{record.status}</Tag>
                <div style={{ fontSize: "0.6rem", textAlign: "right" }} title={dayjs(record.statusTime + "Z").format("DD MMM YYYY h:mm A")}>{dateUtils.formatDateTime(dayjs(record.statusTime + "Z"))}</div>
                <div style={{ fontSize: "0.8rem", textAlign: "right" }}>{record.serviceCenter?.servingSerialNos?.length > 0 && <span> Running {record.serviceCenter.servingSerialNos.join(",")}</span>}</div>
                <div>
                    {record.status === SerialStatus.BOOKED && <Button type="link" className="text-primary pl-0 pr-[2px]" size="large" onClick={() => openSerialEditDialog(record)} > <EditOutlined /></Button>}
                    {record.status === SerialStatus.BOOKED && <Button type="link" className="text-primary pl-[2px] pr-0" size="large" onClick={() => openCancelDialog(record)} > <CloseOutlined /> </Button>}
                </div>
            </div>)
        }
    ];

    return (<>
        <div className="flex justify-between items-center gap-3 pb-1">
            <div className="text-headline">{dayjs().format("ddd, DD MMM YYYY")}</div>
            <Button type="primary" size={"large"} className="flex-end" onClick={openSerialDialog} >
                <PlusOutlined />Book Serial
            </Button>
        </div>
        <div className="w-full">
            <Table
                tableLayout="fixed"
                rowKey="id"
                showHeader={false}
                columns={tableUtils.buildColumns(
                    myServices || [],
                    servicesColumn,
                    null
                )}
                dataSource={myServices}
                size="middle"
                className="ant_table"
                loading={{
                    spinning: (getBookedServicesResult.isLoading) as boolean,
                    indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                }}
                pagination={false}
                locale={{
                    emptyText: <h2 style={{ textAlign: "center" }}>Today you have no appointment</h2>
                }}
            />
        </div>
        <HrnModal
            open={open}
            title={"Book serial"}
            width={520}
            isLoading={bookResult.isLoading}
            submitButtonTitle={"Request for serial"}
            onCancel={closeSerialDialog}
            onSave={createSerial}
            afterClose={closeSerialDialog}
        >
            <div className="mt-6">
                <Form
                    form={serialForm}
                    layout="vertical"
                    name="serialForm"
                    validateMessages={validateMessages}
                >
                    <SerialForm form={serialForm} />
                </Form>
            </div>
        </HrnModal>
        <HrnModal
            open={openCancelForm}
            title={"Cancel Serial"}
            width={520}
            isLoading={updateResult.isLoading}
            submitButtonTitle={"Cancel Serial"}
            isCancelButton={false}
            onCancel={closeCancelDialog}
            onSave={cancelSerial}
            afterClose={closeCancelDialog}
        >
            <div className="mt-6">
                <Form
                    form={cancelForm}
                    layout="vertical"
                    name="cancelForm"
                    validateMessages={validateMessages}
                >
                    <Form.Item name="comment" label={"Please enter the reason or comment"} >
                        <TextArea size="large" placeholder="Reason or comment" />
                    </Form.Item>
                </Form>
            </div>
        </HrnModal>
        <HrnModal
            open={serviceCenter4Detail !== null}
            title={`${serviceCenter4Detail?.serviceCenter?.name}`}
            width={520}
            isLoading={getServiceCenterServicesResult.isFetching}
            submitButtonTitle={"Ok"}
            isCancelButton={false}
            onCancel={() => closeServiceCenterDialog()}
            onSave={() => closeServiceCenterDialog()}
            afterClose={() => closeServiceCenterDialog()}
        >
            <div className="tabs w-full">
                {getServiceCenterServicesResult.isFetching && <>Loading...</>}
                {getServiceCenterServicesResult.isSuccess && <Tabs className="mt-0 mb-0" defaultActiveKey="1" items={[
                    {
                        key: "1",
                        className: "mt-0 mb-0",
                        label: queueServices.length === 0 ? "Queue" : `Queue(${queueServices.length})`,
                        children: <List
                            loading={getServiceCenterServicesResult.isFetching}
                            dataSource={queueServices}
                            renderItem={renderSerialTemplate}
                        />
                    },
                    {
                        key: "2",
                        className: "mt-0 mb-0",
                        label: servedServices.length === 0 ? "Served" : `Served(${servedServices.length})`,
                        children: <List
                            loading={getServiceCenterServicesResult.isFetching}
                            dataSource={servedServices}
                            renderItem={renderSerialTemplate}
                        />
                    },
                ]} />}
            </div>
        </HrnModal>
    </>
    );
};

export default SerialBooking;
